import { FileDownloadOutlined } from "@mui/icons-material";
import { IconButton, Tooltip } from "@mui/material";
import moment from "moment";
import React from "react";
import { useParams } from "react-router-dom";
import { toast } from "react-toastify";
import { useLazyGetDailySalesDetailQuery } from "store/services/reportApi";
import * as XLSX from "xlsx";

const ExportDailyTransactionExcel = ({ activeTab, perPage }) => {
  const { date } = useParams();
  const [fetchDailySalesDetail, { isLoading, isFetching }] =
    useLazyGetDailySalesDetailQuery();

  const handleDownload = async () => {
    try {
      const responseData = await fetchDailySalesDetail({
        date,
        tab: activeTab || "coc",
        page: 1,
        limit: perPage || 10,
      }).unwrap();

      if (!responseData) {
        toast.info("No data.");
        return;
      }

      const sheetData = [];

      if (activeTab === "coc" && responseData.cocCollections?.length) {
        sheetData.push(["COC COLLECTION"]);
        sheetData.push(["SN", "Collection ID", "Shipment Id", "Amount", "Collection Date", "Branch", "Payment Status", "Remarks"]);
        responseData.cocCollections.forEach((row, index) => {
          sheetData.push([
            index + 1,
            row.cocCollectionId || "N/A",
            row.shipmentStringId || "N/A",
            row.shipmentAmount || "0.0",
            row.collectedDate ? moment(row.collectedDate).format("YYYY-MM-DD") : "N/A",
            row.branchName || "N/A",
            row.paymentStatus || "N/A",
            row.remarks || "N/A",
          ]);
        });
      }

      if (activeTab === "delivery" && responseData.deliveryCollections?.length) {
        sheetData.push(["DELIVERY COLLECTION"]);
        sheetData.push(["SN", "Collection ID", "Shipment Id", "Amount", "Collected Date", "Payment Status", "Remarks"]);
        responseData.deliveryCollections.forEach((row, index) => {
          sheetData.push([
            index + 1,
            row.deliveryCollectionId || "N/A",
            row.shipmentId || "N/A",
            row.shipmentAmount || "0.00",
            row.collectedDate ? moment(row.collectedDate).format("YYYY-MM-DD") : "N/A",
            row.paymentStatus || "N/A",
            row.remarks || "N/A",
          ]);
        });
      }

      if (activeTab === "cocQR" && responseData.cocQRPayments?.length) {
        sheetData.push(["COC QR PAYMENTS"]);
        sheetData.push(["SN", "Collection ID", "Shipment Id", "QR Amount", "Payment Date", "Merchant", "Branch", "Payment Status", "Remarks"]);
        responseData.cocQRPayments.forEach((row, index) => {
          sheetData.push([
            index + 1,
            row.cocCollectionId || "N/A",
            row.shipmentStringId || "N/A",
            row.qrAmount || "0.00",
            row.paymentDate ? moment(row.paymentDate).format("YYYY-MM-DD") : "N/A",
            row.merchantName || "N/A",
            row.branchName || "N/A",
            row.paymentStatus || "N/A",
            row.remarks || "N/A",
          ]);
        });
      }

      if (activeTab === "deliveryQR" && responseData.deliveryQRPayments?.length) {
        sheetData.push(["DELIVERY QR PAYMENTS"]);
        sheetData.push(["SN", "Collection ID", "Shipment Id", "QR Amount", "Payment Date", "Merchant", "Payment Status", "Remarks"]);
        responseData.deliveryQRPayments.forEach((row, index) => {
          sheetData.push([
            index + 1,
            row.deliveryCollectionId || "N/A",
            row.shipmentId || "N/A",
            row.qrAmount || "0.00",
            row.paymentDate ? moment(row.paymentDate).format("YYYY-MM-DD") : "N/A",
            row.merchantName || "N/A",
            row.paymentStatus || "N/A",
            row.remarks || "N/A",
          ]);
        });
      }

      if (activeTab === "cod" && responseData.codTransfers?.length) {
        sheetData.push(["COD TRANSFERS"]);
        sheetData.push(["SN", "COD ID", "Shipment Id", "Vendor", "Amount", "Transfer Date", "Remarks"]);
        responseData.codTransfers.forEach((row, index) => {
          sheetData.push([
            index + 1,
            row.codTransferId || "N/A",
            Array.isArray(row?.shipment) && row.shipment.length > 0 ? row.shipment.join(", ") : "N/A",
            row.vendorName || "N/A",
            row.transferAmount || "N/A",
            row.transferDate ? moment(row.transferDate).format("YYYY-MM-DD") : "N/A",
            row.remarks || "N/A",
          ]);
        });
      }

      if (activeTab === "deduct" && responseData.deductTransfers?.length) {
        sheetData.push(["COD CHARGE COLLECTION"]);
        sheetData.push(["SN", "COD ID", "Shipment ID", "Vendor", "Deduct Charge", "Transfer Date", "Payment Status", "Remarks"]);
        responseData.deductTransfers.forEach((row, index) => {
          sheetData.push([
            index + 1,
            row.codTransferId || "N/A",
            row.shipmentStringId || "N/A",
            row.vendorName || "N/A",
            row.deductChargeFromCOD || "0.00",
            row.transferDate ? moment(row.transferDate).format("YYYY-MM-DD") : "N/A",
            row.paymentStatus || "N/A",
            row.remarks || "N/A",
          ]);
        });
      }

      if (sheetData.length === 0) {
        toast.info("No data to export.");
        return;
      }

      const tabLabels = {
        coc: "COC_Collections",
        delivery: "Delivery_Collections",
        cocQR: "COC_QR_Payments",
        deliveryQR: "Delivery_QR_Payments",
        cod: "COD_Transfers",
        deduct: "COD_Charge_Collection",
      };

      const worksheet = XLSX.utils.aoa_to_sheet(sheetData);
      const workbook = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(workbook, worksheet, "data");
      const excelBuffer = XLSX.write(workbook, { bookType: "xlsx", type: "array" });
      const blob = new Blob([excelBuffer], {
        type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet;charset=UTF-8",
      });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `Daily_Transaction_Report_${date}_${tabLabels[activeTab] || activeTab}.xlsx`;
      a.click();
      URL.revokeObjectURL(url);

      toast.success("Data downloaded successfully");
    } catch (error) {
      toast.error("Error fetching daily transaction data: " + error.message);
    }
  };

  return (
    <div>
      <Tooltip title="Export Excel" arrow>
        <IconButton
          color="success"
          onClick={handleDownload}
          disabled={isLoading || isFetching}
        >
          <FileDownloadOutlined />
        </IconButton>
      </Tooltip>
    </div>
  );
};

export default ExportDailyTransactionExcel;
